/**
 * FlowDiagramSVG & DiagramScaffold
 *
 * Two deterministic SVG components that replace placeholder text in TeachBud:
 *
 * FlowDiagramSVG — Parses a natural-language imageHint and renders a matter-flow /
 *   food-web diagram. Used in PresentationGenerator to replace the 🖼️ placeholder.
 *
 * DiagramScaffold — Extracts organisms/entities from a student prompt and renders
 *   pre-labeled circles with blank arrows for students to complete. Used in the
 *   Activity Sheet diagram workspace instead of a plain blank box.
 */

import React, { useId } from 'react';

// ── Entity classification ─────────────────────────────────────────────────────

const PRODUCER_RX    = /\b(grass|plant|bean|corn|oak|leaf|leaves|algae|seaweed|wheat|grain|seed|flower|tree|shrub|moss|fern|phytoplankton|kelp|hyacinth|bamboo|herbs?|clover|lily)\b/i;
const DECOMPOSER_RX  = /\b(fung|fungi|fungus|decomposer|bacteria|mushroom|mold|earthworm|microbe|microorganism|yeast)\b/i;
const ENVIRONMENT_RX = /\b(soil|air|water|carbon\s*dioxide|co2|sunlight|sun|ground|environment|atmosphere|minerals?|nutrients?|energy)\b/i;

type NodeKind = 'producer' | 'consumer' | 'decomposer' | 'environment';

export function classifyOrganism(label: string): NodeKind {
  if (PRODUCER_RX.test(label))    return 'producer';
  if (DECOMPOSER_RX.test(label))  return 'decomposer';
  if (ENVIRONMENT_RX.test(label)) return 'environment';
  return 'consumer';
}

export const NODE_COLORS: Record<NodeKind, { fill: string; stroke: string; text: string }> = {
  producer:    { fill: '#dcfce7', stroke: '#16a34a', text: '#166534' },
  consumer:    { fill: '#dbeafe', stroke: '#2563eb', text: '#1e40af' },
  decomposer:  { fill: '#fef3c7', stroke: '#d97706', text: '#92400e' },
  environment: { fill: '#f1f5f9', stroke: '#64748b', text: '#334155' },
};

// ── Parser for imageHint text ─────────────────────────────────────────────────

function normalizeLabel(raw: string): string {
  return raw
    .replace(/\b(matter|the|a|an|1|2|3|one|two|three|dead|live|living|small|large|tiny|giant|common|blade\s+of|piece[s]?\s+of)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

interface ParsedGraph {
  nodes: string[];
  edges: Array<{ from: string; to: string }>;
}

function parseImageHint(hint: string): ParsedGraph {
  const seen  = new Set<string>();
  const nodes: string[] = [];
  const edges: Array<{ from: string; to: string }> = [];

  function addNode(raw: string): string {
    const s = normalizeLabel(raw);
    if (s && !seen.has(s)) { seen.add(s); nodes.push(s); }
    return normalizeLabel(raw);
  }
  function addEdge(f: string, t: string) {
    const a = normalizeLabel(f), b = normalizeLabel(t);
    if (a && b && a !== b) edges.push({ from: a, to: b });
  }

  // Pattern A: "X branching to Y and to Z"
  const branchMatch = hint.match(/^(.+?)\s+branching\s+to\s+(.+)/i);
  if (branchMatch) {
    const source  = addNode(branchMatch[1]);
    const targets = branchMatch[2]
      .split(/\s*(?:and\s+to\s+|and\s+|\+\s*|,\s*)\s*/i)
      .map(t => addNode(t))
      .filter(Boolean);
    for (const t of targets) addEdge(source, t);
    return { nodes, edges };
  }

  // Pattern B: "from X to Y to Z" or "from X, Y to Z"
  const fromMatch = hint.match(/^(?:matter\s+)?(?:moving\s+)?from\s+(.+?)(?:\s+to\s+(.+))?$/i);
  if (fromMatch && fromMatch[2]) {
    const sources = fromMatch[1].split(/\s*,\s*|\s+and\s+/i).map(addNode).filter(Boolean);
    const rest    = fromMatch[2].split(/\s+to\s+/i).map(addNode).filter(Boolean);
    let prev = sources[sources.length - 1];
    for (const t of rest) { addEdge(prev, t); prev = t; }
    for (const s of sources.slice(0, -1)) addEdge(s, rest[0]);
    return { nodes, edges };
  }

  // Pattern C: chain "X to Y to Z" or "X → Y → Z"
  const chainParts = hint.split(/\s+(?:to|→)\s+/i);
  if (chainParts.length >= 2) {
    // Each segment may contain multiple comma-separated entities
    const groups = chainParts.map(p =>
      p.split(/\s*,\s*|\s+and\s+/i).map(addNode).filter(Boolean)
    );
    for (let i = 0; i < groups.length - 1; i++) {
      for (const f of groups[i]) {
        for (const t of groups[i + 1]) addEdge(f, t);
      }
    }
    return { nodes, edges };
  }

  // Fallback: comma-separated list, no edges
  hint.split(/[,;]+/).map(p => addNode(p.trim()));
  return { nodes, edges };
}

// ── Layout engine ─────────────────────────────────────────────────────────────

const NW = 120;  // node width
const NH = 42;   // node height
const NR = 12;   // node border-radius
const HG = 52;   // horizontal gap between nodes
const VG = 44;   // vertical gap between rows
const PD = 20;   // canvas padding

interface LayoutNode {
  id: string;
  label: string;
  kind: NodeKind;
  x: number;
  y: number;
}

interface DiagramLayout {
  nodes: LayoutNode[];
  edges: Array<{ from: string; to: string }>;
  width: number;
  height: number;
}

function buildLayout(graph: ParsedGraph): DiagramLayout {
  const { nodes, edges } = graph;
  if (nodes.length === 0) return { nodes: [], edges: [], width: 240, height: 80 };

  const outDeg: Record<string, number> = {};
  const inDeg:  Record<string, number> = {};
  for (const n of nodes) { outDeg[n] = 0; inDeg[n] = 0; }
  for (const e of edges) {
    outDeg[e.from] = (outDeg[e.from] ?? 0) + 1;
    inDeg[e.to]    = (inDeg[e.to]    ?? 0) + 1;
  }

  const isBranching = Object.values(outDeg).some(d => d > 1);
  const lNodes: LayoutNode[] = [];

  if (isBranching) {
    // Tree layout: source on left, targets stacked vertically on right
    const source   = nodes.find(n => inDeg[n] === 0) ?? nodes[0];
    const targets  = edges.filter(e => e.from === source).map(e => e.to);
    const orphans  = nodes.filter(n => n !== source && !targets.includes(n));
    const allRight = [...targets, ...orphans];
    const totalRH  = allRight.length * (NH + VG) - VG;
    const sourceCY = Math.max(totalRH / 2, NH / 2);

    lNodes.push({ id: source, label: source, kind: classifyOrganism(source), x: PD, y: sourceCY - NH / 2 });
    allRight.forEach((t, i) => {
      lNodes.push({ id: t, label: t, kind: classifyOrganism(t), x: PD + NW + HG, y: i * (NH + VG) });
    });
    return {
      nodes: lNodes, edges,
      width:  PD + NW + HG + NW + PD,
      height: Math.max(totalRH, NH) + PD,
    };
  }

  // Linear / horizontal chain
  if (nodes.length <= 4) {
    nodes.forEach((n, i) => {
      lNodes.push({ id: n, label: n, kind: classifyOrganism(n), x: PD + i * (NW + HG), y: PD });
    });
    return {
      nodes: lNodes, edges,
      width:  PD + nodes.length * (NW + HG) - HG + PD,
      height: PD + NH + PD,
    };
  }

  // Two-row wrap for 5+ nodes
  const half = Math.ceil(nodes.length / 2);
  const r1 = nodes.slice(0, half);
  const r2 = nodes.slice(half);
  r1.forEach((n, i) => lNodes.push({ id: n, label: n, kind: classifyOrganism(n), x: PD + i * (NW + HG), y: PD }));
  r2.forEach((n, i) => lNodes.push({ id: n, label: n, kind: classifyOrganism(n), x: PD + i * (NW + HG), y: PD + NH + VG }));
  return {
    nodes: lNodes, edges,
    width:  PD + Math.max(r1.length, r2.length) * (NW + HG) - HG + PD,
    height: PD + 2 * NH + VG + PD,
  };
}

// ── Arrow path ────────────────────────────────────────────────────────────────

function ArrowPath({
  from, to, nodeMap, markerId,
}: {
  from: string; to: string;
  nodeMap: Map<string, LayoutNode>;
  markerId: string;
}) {
  const f = nodeMap.get(from);
  const t = nodeMap.get(to);
  if (!f || !t) return null;

  const fx = f.x + NW;      const fy = f.y + NH / 2;
  const tx = t.x;           const ty = t.y + NH / 2;
  const dx = tx - fx;       const dy = ty - fy;
  const isHoriz = Math.abs(dx) >= Math.abs(dy);

  let d: string;
  if (isHoriz) {
    const mx = fx + dx / 2;
    d = `M ${fx} ${fy} C ${mx} ${fy}, ${mx} ${ty}, ${tx} ${ty}`;
  } else {
    // Branching elbow
    const ex = f.x + NW + HG / 2;
    d = `M ${fx} ${fy} L ${ex} ${fy} L ${ex} ${ty} L ${tx} ${ty}`;
  }

  const lx = (fx + tx) / 2;
  const ly = isHoriz ? Math.min(fy, ty) - 5 : (fy + ty) / 2;

  return (
    <g>
      <path d={d} fill="none" stroke="#94a3b8" strokeWidth={1.5}
        markerEnd={`url(#${markerId})`} />
      {isHoriz && Math.abs(dx) > 40 && (
        <text x={lx} y={ly} textAnchor="middle" fontSize={8.5}
          fill="#94a3b8" fontFamily="system-ui,sans-serif" fontStyle="italic">
          matter
        </text>
      )}
    </g>
  );
}

// ── FlowDiagramSVG ────────────────────────────────────────────────────────────

export interface FlowDiagramSVGProps {
  /** Natural language description, e.g. "grass to grasshopper to frog" */
  imageHint: string;
  /** Scale down for thumbnail / non-fullscreen display */
  compact?: boolean;
  className?: string;
}

export function FlowDiagramSVG({ imageHint, compact = false, className = '' }: FlowDiagramSVGProps) {
  const uid      = useId().replace(/:/g, '');
  const markerId = `arr-${uid}`;
  const graph    = parseImageHint(imageHint);
  const layout   = buildLayout(graph);
  const nodeMap  = new Map(layout.nodes.map(n => [n.id, n]));

  if (layout.nodes.length === 0) return null;

  const scale = compact ? 0.72 : 1;

  // Kinds that actually appear in this diagram (for legend)
  const kindsPresent = [...new Set(layout.nodes.map(n => n.kind))];

  return (
    <figure className={`flex flex-col items-center gap-2 ${className}`}>
      <svg
        width={layout.width * scale}
        height={layout.height * scale}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        style={{ maxWidth: '100%', overflow: 'visible' }}
        role="img"
        aria-label={imageHint}
      >
        <defs>
          <marker id={markerId} markerWidth="8" markerHeight="6"
            refX="7" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
          </marker>
        </defs>

        {layout.edges.map((e, i) => (
          <ArrowPath key={i} from={e.from} to={e.to} nodeMap={nodeMap} markerId={markerId} />
        ))}

        {layout.nodes.map(n => {
          const c = NODE_COLORS[n.kind];
          const display = n.label.length > 14 ? n.label.slice(0, 13) + '…' : n.label;
          return (
            <g key={n.id}>
              <rect x={n.x} y={n.y} width={NW} height={NH}
                rx={NR} ry={NR}
                fill={c.fill} stroke={c.stroke} strokeWidth={1.5} />
              <text
                x={n.x + NW / 2} y={n.y + NH / 2 + 1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={10.5} fontWeight={600}
                fill={c.text} fontFamily="system-ui,sans-serif"
              >
                {display}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Color legend */}
      {kindsPresent.length >= 2 && (
        <div className="flex flex-wrap gap-3 justify-center">
          {([ 'producer', 'consumer', 'decomposer', 'environment' ] as NodeKind[])
            .filter(k => kindsPresent.includes(k))
            .map(k => (
              <span key={k} className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide"
                style={{ color: NODE_COLORS[k].text }}>
                <span className="inline-block w-2.5 h-2.5 rounded-full border"
                  style={{ backgroundColor: NODE_COLORS[k].fill, borderColor: NODE_COLORS[k].stroke }} />
                {k}
              </span>
            ))}
        </div>
      )}
    </figure>
  );
}

// ── DiagramScaffold ───────────────────────────────────────────────────────────

/**
 * Extracts organism/entity names from a student prompt that uses patterns like:
 *   "using 1 blade of grass, 1 grasshopper, and 1 frog"
 *   "with 1 oak leaf, 1 caterpillar, 1 bird, and 1 fungus"
 *   "that includes 1 plant, 1 deer, 1 wolf, and soil"
 */
export function extractOrganismsFromPrompt(prompt: string): string[] {
  // Try "using/with/includes X, Y, and Z" pattern
  const listMatch = prompt.match(
    /\b(?:using|with|include[sd]?|that\s+includes?)\b\s+(.+?)(?:\.\s|\.\s*$|Draw|Show|Label|Add\s|Describe|Write|$)/is
  );
  if (!listMatch) return [];

  return listMatch[1]
    .split(/\s*,\s*|\s+and\s+/i)
    .map(s =>
      s
        .replace(/\b\d+\s*/g, '')                       // leading numbers
        .replace(/\bblades?\s+of\s+/gi, '')             // "blade of"
        .replace(/\bpieces?\s+of\s+/gi, '')             // "piece of"
        .replace(/\b(a|an|the|small|large|dead|live)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter(s => s.length > 1)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .slice(0, 7); // max 7 entities
}

// Scaffold node dimensions
const SW = 92;   // scaffold node width
const SH = 36;   // scaffold node height
const SG = 40;   // scaffold node gap

export interface DiagramScaffoldProps {
  prompt: string;
  /** Minimum height for the container in px */
  height: number;
}

/**
 * Renders pre-labeled organism nodes with dashed blank arrows for students
 * to complete. Shown in activity sheet diagram workspaces instead of a
 * plain blank box when the prompt contains a list of organisms.
 */
export function DiagramScaffold({ prompt, height }: DiagramScaffoldProps) {
  const organisms = extractOrganismsFromPrompt(prompt);

  if (organisms.length < 2) {
    // Fallback: plain blank box (no organisms detected)
    return (
      <div
        className="as-workspace rounded-lg border border-dashed border-slate-200"
        style={{ height, backgroundColor: 'rgba(248,250,252,0.2)' }}
      />
    );
  }

  const maxPerRow = 4;
  const rows: string[][] = [];
  for (let i = 0; i < organisms.length; i += maxPerRow) {
    rows.push(organisms.slice(i, i + maxPerRow));
  }

  const rowCount = rows.length;
  const maxCols  = Math.max(...rows.map(r => r.length));
  const svgW     = PD + maxCols * (SW + SG) - SG + PD;
  const svgH     = PD + rowCount * (SH + VG) - VG + PD;

  return (
    <div
      className="rounded-lg border border-dashed border-slate-300 bg-slate-50/20 flex flex-col"
      style={{ minHeight: Math.max(height, svgH + 36) }}
    >
      <p className="px-3 pt-2 text-[9px] text-slate-400 font-semibold uppercase tracking-widest">
        Use these organisms — draw arrows to show where matter moves
      </p>
      <div className="flex-1 flex items-start justify-center px-2 pb-2 pt-1">
        <svg
          width={svgW} height={svgH}
          viewBox={`0 0 ${svgW} ${svgH}`}
          style={{ maxWidth: '100%', overflow: 'visible' }}
          aria-hidden="true"
        >
          <defs>
            <marker id="scaffold-arr" markerWidth="7" markerHeight="5"
              refX="6" refY="2.5" orient="auto">
              <polygon points="0 0, 7 2.5, 0 5" fill="#cbd5e1" />
            </marker>
          </defs>

          {rows.map((row, ri) => {
            const rowY = PD + ri * (SH + VG);
            return (
              <g key={ri}>
                {/* Dashed guide arrows between nodes in each row */}
                {row.slice(0, -1).map((_, ci) => {
                  const x1 = PD + ci * (SW + SG) + SW + 4;
                  const x2 = PD + (ci + 1) * (SW + SG) - 4;
                  const y  = rowY + SH / 2;
                  return (
                    <line key={ci}
                      x1={x1} y1={y} x2={x2} y2={y}
                      stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="4,3"
                      markerEnd="url(#scaffold-arr)" />
                  );
                })}

                {/* Organism nodes */}
                {row.map((org, ci) => {
                  const x    = PD + ci * (SW + SG);
                  const kind = classifyOrganism(org);
                  const c    = NODE_COLORS[kind];
                  const disp = org.length > 12 ? org.slice(0, 11) + '…' : org;
                  return (
                    <g key={org}>
                      <rect x={x} y={rowY} width={SW} height={SH}
                        rx={SH / 2} ry={SH / 2}
                        fill={c.fill} stroke={c.stroke} strokeWidth={1.5} />
                      <text
                        x={x + SW / 2} y={rowY + SH / 2 + 1}
                        textAnchor="middle" dominantBaseline="middle"
                        fontSize={9.5} fontWeight={600}
                        fill={c.text} fontFamily="system-ui,sans-serif"
                      >
                        {disp}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

export default FlowDiagramSVG;
